import React, { useEffect, useState } from 'react'

import getVehicles, {
    GetVehiclesOptions,
    Vehicle,
} from 'api/vehicles/getVehicles'
import { FormikDropdown } from 'components/FormikFields'
import { FormikDropdownChild } from 'components/FormikFields/FormikDropdown/FormikDropdown'
import { setFieldValueAndTouched } from 'form/fields/utils/fieldUtils'
import AssetType from 'state/enums/AssetType'

function getDropdownName(vehicleNumber: number) {
    return vehicleNumber === 1
        ? 'vehicle.primary.selectedVehicleId'
        : 'vehicle.secondary.selectedVehicleId'
}

export function constructVehicleDropdownValue<
    V extends { id: number; vehicleType: string; uuid: string },
>(vehicle: V) {
    return `${vehicle.id}:${vehicle.vehicleType}:${vehicle.uuid}`
}

interface queryProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any
    lenderId?: number
    vehicleId?: number
    disable?: boolean
    dataSource?: string[]
    assetType?: unknown
    primaryVehicleId?: number
    vehicleNumber: number
    prefilled?: boolean
    captive?: string
}

const defaultProps = {
    lenderId: null,
    vehicleId: null,
    disable: null,
    dataSource: null,
    assetType: null,
    primaryVehicleId: null,
    prefilled: false,
    captive: undefined,
}

export default function VehicleDropdown({
    formik,
    lenderId,
    vehicleId,
    disable,
    dataSource,
    assetType,
    primaryVehicleId,
    vehicleNumber,
    prefilled,
    captive,
}: queryProps) {
    const [response, updateResponse] = useState([] as Vehicle[])
    const { vehicle } = formik.values
    const { condition } = vehicle
    const DROPDOWN_NAME = getDropdownName(vehicleNumber)
    const FIRST_VEHICLE = 1
    const IS_FIRST_VEHICLE = vehicleNumber === FIRST_VEHICLE

    const vehicleFieldName = IS_FIRST_VEHICLE
        ? 'vehicle.primary.selectedVehicleId'
        : 'vehicle.secondary.selectedVehicleId'

    const year = IS_FIRST_VEHICLE
        ? vehicle.primary.year
        : vehicle.secondary.year

    const { makeId } = vehicle

    const modelId = IS_FIRST_VEHICLE
        ? vehicle.primary.modelId
        : vehicle.secondary.modelId

    const isVehicleSelectionIncomplete =
        !modelId || !year || !makeId || modelId === '0'

    const selectedVehicle = IS_FIRST_VEHICLE
        ? vehicle.primary.selectedVehicleId
        : vehicle.secondary.selectedVehicleId

    const assetVehicleId = IS_FIRST_VEHICLE
        ? 'vehicle.primary'
        : 'vehicle.secondary'

    const isOpeAsset = assetType === AssetType.OUTDOOR_POWER_EQUIPMENT

    function populateVehicleFields(matchedVehicle: Vehicle) {
        setFieldValueAndTouched(
            formik,
            vehicleFieldName,
            constructVehicleDropdownValue(matchedVehicle),
        )

        setFieldValueAndTouched(
            formik,
            'vehicle.vehicleType',
            matchedVehicle.vehicleType,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'rv'}`,
            matchedVehicle.id,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'model'}`,
            matchedVehicle.model,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'rv_type'}`,
            matchedVehicle.rvType,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'manufacturer_price'}`,
            matchedVehicle.manufacturerPrice,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'wholesale_price'}`,
            matchedVehicle.wholesalePrice,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'retail_price'}`,
            matchedVehicle.retailPrice,
        )

        setFieldValueAndTouched(
            formik,
            `${assetVehicleId}.${'condition'}`,
            condition,
        )

        if (isOpeAsset) {
            setFieldValueAndTouched(
                formik,
                `${assetVehicleId}.${'equipmentType'}`,
                matchedVehicle.vehicleType,
            )

            setFieldValueAndTouched(
                formik,
                `${assetVehicleId}.${'equipmentSubType'}`,
                matchedVehicle.equipmentSubType,
            )

            setFieldValueAndTouched(
                formik,
                `${assetVehicleId}.${'msrp'}`,
                matchedVehicle.msrp,
            )

            setFieldValueAndTouched(
                formik,
                `${assetVehicleId}.${'asset_value'}`,
                matchedVehicle.msrp,
            )

            setFieldValueAndTouched(
                formik,
                `${assetVehicleId}.sku`,
                matchedVehicle.sku,
            )

            setFieldValueAndTouched(
                formik,
                `${assetVehicleId}.oem`,
                matchedVehicle.oem,
            )
        }
    }

    useEffect(() => {
        const abortController = new AbortController()

        if (isVehicleSelectionIncomplete && !prefilled) {
            if (selectedVehicle) {
                formik.setFieldValue(DROPDOWN_NAME, '')
            }

            updateResponse([])

            return () => abortController.abort()
        }

        const currentPrimaryVehicleValue =
            formik.values.vehicle.primary.selectedVehicleId

        const currentPrimaryVehicleValueParts =
            currentPrimaryVehicleValue?.split(':')

        const currentPrimaryVehicleId = currentPrimaryVehicleValueParts?.[0]
        const currentVehicleTypeValue = currentPrimaryVehicleValueParts?.[1]

        if (condition && year && makeId && modelId) {
            const getVehiclesOptions = {
                condition,
                year,
                makeId,
                modelId,
                vehicleId,
                lenderId,
                disable,
                dataSource,
                assetType,
                primaryVehicleId,
                captive,
            } as GetVehiclesOptions

            if (!IS_FIRST_VEHICLE) {
                getVehiclesOptions.primaryVehicleId = currentPrimaryVehicleId
            }

            updateResponse([])

            getVehicles(getVehiclesOptions, abortController)
                .then((vehicles: Vehicle[]) => {
                    updateResponse(vehicles)

                    const currentSecondaryVehicleValue =
                        formik.values.vehicle.secondary.selectedVehicleId

                    const currentVehicleValue = IS_FIRST_VEHICLE
                        ? currentPrimaryVehicleValue
                        : currentSecondaryVehicleValue

                    const currentValueInResponse = vehicles.find(
                        (v) =>
                            constructVehicleDropdownValue(v) ===
                            currentVehicleValue,
                    )

                    if (!currentValueInResponse && !prefilled) {
                        formik.setFieldValue(DROPDOWN_NAME, '')

                        const currentVehicleTypeMatch =
                            formik.values.vehicle.vehicleType ===
                            currentVehicleTypeValue

                        if (IS_FIRST_VEHICLE && !currentVehicleTypeMatch) {
                            formik.setFieldValue('vehicle.vehicleType', '')
                        }

                        if (isOpeAsset) {
                            formik.setFieldValue(
                                `${assetVehicleId}.equipmentType`,
                                '',
                            )

                            formik.setFieldValue(
                                `${assetVehicleId}.equipmentSubType`,
                                '',
                            )

                            formik.setFieldValue(`${assetVehicleId}.msrp`, '')

                            formik.setFieldValue(
                                `${assetVehicleId}.asset_value`,
                                '',
                            )

                            formik.setFieldValue(`${assetVehicleId}.sku`, '')
                            formik.setFieldValue(`${assetVehicleId}.oem`, '')
                        }
                    }

                    if (vehicles.length === 1) {
                        populateVehicleFields(vehicles[0])
                    }
                })
                .catch(() => {})
        }

        return () => abortController.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition, year, makeId, modelId, assetType])

    if (isVehicleSelectionIncomplete && !prefilled) {
        return (
            <FormikDropdown
                name={DROPDOWN_NAME}
                label="Vehicle"
                placeholder={prefilled ? '' : 'Select Vehicle'}
                disabled
            >
                {{ name: '', value: '' }}
                {{ name: '', value: '' }}
            </FormikDropdown>
        )
    }

    const vehicleName = IS_FIRST_VEHICLE
        ? vehicle.primary.model
        : vehicle.secondary?.model

    if (prefilled && vehicleName) {
        return (
            <FormikDropdown
                name={DROPDOWN_NAME}
                label="Vehicle"
                placeholder=""
                prefilled={prefilled}
            >
                {{ name: vehicleName, value: selectedVehicle }}
                {{ name: vehicleName, value: selectedVehicle }}
            </FormikDropdown>
        )
    }

    return (
        <FormikDropdown
            name={DROPDOWN_NAME}
            label="Vehicle"
            placeholder={prefilled ? '' : 'Select Vehicle'}
            disabled={response.length === 0}
            error={selectedVehicle === null || selectedVehicle === ''}
            onChange={(val: string) => {
                const matchedVehicle = response.find(
                    (v) => constructVehicleDropdownValue(v) === val,
                )

                if (matchedVehicle) {
                    populateVehicleFields(matchedVehicle)
                }
            }}
            searchable
            searchPlaceholder="Search vehicles..."
            prefilled={prefilled}
        >
            {response.map((child: Vehicle) => {
                const newBorn: FormikDropdownChild = {
                    name: child.name,
                    value: constructVehicleDropdownValue(child),
                }

                return newBorn
            })}
        </FormikDropdown>
    )
}

VehicleDropdown.defaultProps = defaultProps
